import { Box, Typography, Button } from '@mui/material';
import { useState, useEffect } from 'react';
import ElectricBoltOutlinedIcon from '@mui/icons-material/ElectricBoltOutlined';
import ErrorIcon from '@mui/icons-material/Error';
import CreateOutlinedIcon from '@mui/icons-material/CreateOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { ThemeProvider } from '@mui/material/styles';
import { router } from '@inertiajs/react';
import { keyframes } from '@mui/system';
import theme from '../styles/theme';
import union_logo from '../assets/Union.svg';
import SecondaryButton from './SecondaryButton';
import { trackFeatureUsage } from '../services/FeatureTrackingService';
import { useAuth } from '../hooks/useAuth';

const waveAnimation = keyframes`
  0% { transform: rotate(0deg); }
  25% { transform: rotate(5deg); }
  50% { transform: rotate(0deg); }
  75% { transform: rotate(-5deg); }
  100% { transform: rotate(0deg); }
`;

interface ListingProcessingStepProps {
  status: 'processing' | 'uploading' | 'success' | 'error';
  onBack: () => void;
  listingId?: string;
  aiEmail?: string;
}

const ListingProcessingStep: React.FC<ListingProcessingStepProps> = ({
  status,
  onBack,
  listingId,
  aiEmail,
}) => {
  const [showUploadMessage, setShowUploadMessage] = useState(false);
  const [copied, setCopied] = useState(false);
  const [localAiEmail, setLocalAiEmail] = useState<string | undefined>(aiEmail);
  const [showGoBackButton, setShowGoBackButton] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    if (!aiEmail || aiEmail === '') {
      const stored = localStorage.getItem('ai-email');
      if (stored) setLocalAiEmail(stored);
    } else {
      setLocalAiEmail(aiEmail);
    }
  }, [aiEmail]);

  useEffect(() => {
    if (status === 'processing') {
      const timer = setTimeout(() => {
        setShowUploadMessage(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [status]);

  // Show Go Back button after 10 seconds
  useEffect(() => {
    if (status === 'processing' || status === 'uploading') {
      const timer = setTimeout(() => {
        setShowGoBackButton(true);
      }, 15000); // 15 seconds
      return () => clearTimeout(timer);
    } else {
      setShowGoBackButton(false);
    }
  }, [status]);

  const handleSuccess = async () => {
    const localStorageListingId = localStorage.getItem('listing-id');
    const memoryListingId = listingId;
    let redirectListingId = ""

    if (memoryListingId && memoryListingId !== "") {
      redirectListingId = `/listings/${memoryListingId}`;
    } else if (localStorageListingId && localStorageListingId !== "") {
      redirectListingId = `/listings/${localStorageListingId}`;
    } else {
      redirectListingId = "/home";
    }

    // Clean up localStorage
    localStorage.removeItem('listingOnboardingFormData');
    localStorage.removeItem('listingOnboardingStep');
    localStorage.removeItem('listing-create-processing');
    localStorage.removeItem('listing-id');
    localStorage.removeItem('pending-listing-id');
    localStorage.removeItem('ai-email');

    console.log('Listing created successfully, form data and images cleared from storage');
    router.visit(redirectListingId);
  };

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          padding: { xs: 2, sm: 4 },
          backgroundColor: 'white'
        }}
      >
          {/* Go Back button - shows after 10 seconds */}
        {(showGoBackButton || status === 'error') && (status === 'processing' || status === 'uploading' || status === 'error') && (
          <>
          <Typography variant="h6" gutterBottom sx={{ mt: 2 }} color="error.main">
            Oh no! An unexpected error occurred.
          </Typography>
          <ErrorIcon
                sx={{
                  fontSize: 48,
                  color: 'error.main',
                  animation: 'shake 0.5s ease-in-out',
                  '@keyframes shake': {
                    '0%, 100%': { transform: 'translateX(0)' },
                    '25%': { transform: 'translateX(-5px)' },
                    '75%': { transform: 'translateX(5px)' }
                  }
                }}
              />
            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={onBack}
                sx={{
                  borderColor: 'black',
                  color: 'black',
                  '&:hover': {
                    borderColor: 'black',
                    backgroundColor: '#f5f5f5',
                  }
                }}
              >
                Go Back & Try Again
              </Button>
              <Typography variant="subtitle1" sx={{ display: 'block', mt: 1, color: '#666', fontWeight: 350 }}>
                You can safely go back and try again.
              </Typography>
              <Typography variant="subtitle1" sx={{ display: 'block', mt: 1, color: '#666', fontWeight: 350 }}>
                Please contact support at rich@heyyproperty.com, if you continue to see this message.
              </Typography>
            </Box>
          </>
        )}
        {(!showGoBackButton && (status === 'processing' || status === 'uploading')) && (
          <>
            {showUploadMessage ? (
              <ElectricBoltOutlinedIcon
                sx={{
                  fontSize: 48,
                  color: 'black',
                  animation: 'bounce 1s infinite',
                  '@keyframes bounce': {
                    '0%, 100%': { transform: 'translateY(0)' },
                    '50%': { transform: 'translateY(-10px)' }
                  }
                }}
              />
            ) : (
              <CreateOutlinedIcon
                sx={{
                  fontSize: 48,
                  color: 'black',
                  animation: 'bounce 1s infinite',
                  '@keyframes bounce': {
                    '0%, 100%': { transform: 'translateY(0)' },
                    '50%': { transform: 'translateY(-10px)' }
                  }
                }}
              />
            )}
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              {showUploadMessage ? 'Uploading photos...' : 'Working on your listing...'}
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              {showUploadMessage
                ? 'This may take a few moments depending on the number of photos.'
                : 'Please wait while we set up your listing details.'}
            </Typography>

            {/* Do not refresh warning */}
            <Box
              sx={{
                backgroundColor: '#fff3cd',
                border: '1px solid #ffeaa7',
                borderRadius: 1,
                p: 2,
                mt: 2,
                maxWidth: '400px',
                textAlign: 'center'
              }}
            >
              <Typography variant="body2" sx={{ color: '#856404', fontWeight: 500 }}>
                ⚠️ Please do not refresh this page
              </Typography>
              <Typography variant="caption" sx={{ color: '#856404', display: 'block', mt: 0.5 }}>
                Your listing is being processed. Refreshing may interrupt the process.
              </Typography>
            </Box>
          </>
        )}

        {status === 'success' && (
          <>
            <Box
              component="img"
              src={union_logo}
              alt="Union Logo"
              sx={{
                display: 'block',
                margin: '0 auto',
                width: '120px',
                marginBottom: '2rem',
                animation: `${waveAnimation} 2s ease-in-out infinite`
              }}
            />
            <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 2, textAlign: 'left' }}>
              Your listing will go live in 3 hours!
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                background: '#f5f5f5',
                borderRadius: 2,
                p: 3,
                mb: 4,
                maxWidth: { xs: '85%', sm: 420 },
                width: { xs: '85%', sm: 'auto' },
                margin: '0 auto',
                boxShadow: 1
              }}
            >
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', mb: 2, fontWeight: 600, justifyContent: 'center' }}>
                Did you already create a listing? Add your new AI to the description.
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'center', sm: 'center' }, mb: 1, width: '100%' }}>
                <Typography variant="body2" sx={{ fontSize: 17, fontWeight: 350, textAlign: 'center' }}>{localAiEmail}</Typography>
                <Box
                  sx={{
                    mt: { xs: 1, sm: 0 },
                    ml: { xs: 0, sm: 2 },
                    display: 'flex',
                    justifyContent: { xs: 'center', sm: 'flex-start' },
                    width: { xs: 'auto', sm: 'auto' }
                  }}
                >
                  <SecondaryButton
                    variant="contained"
                    size="small"
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      borderRadius: 2,
                      boxShadow: 'none',
                      border: '1px solid black',
                      color: 'black',
                      background: 'white',
                      width: { xs: 'auto', sm: 'auto' },
                      '&:hover': {
                        background: '#f0f0f0',
                        border: '1px solid black',
                        borderRadius: 2,
                        color: 'black',
                      }
                    }}
                    onClick={() => {
                      navigator.clipboard.writeText(localAiEmail ?? '');
                      setCopied(true);
                      setTimeout(() => setCopied(false), 2000);
                      if (user?.id) {
                        trackFeatureUsage(user.id, 'Copy AI Email');
                      }
                    }}
                  >
                    Copy
                  </SecondaryButton>
                </Box>
              </Box>
              {copied && (
                <Typography variant="caption" sx={{ color: 'green', mb: 1 }}>
                  Copied, now add it to your listings
                </Typography>
              )}
              <Box sx={{ mt: 2, width: '100%'  }}>
                <ul style={{ paddingLeft: 0, margin: 0, listStyle: 'none' }}>
                  <li style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
                    <span style={{ fontSize: 30, marginRight: 8 }}>💬</span>
                    <Typography variant="body2" sx={{ fontSize: 17, fontWeight: 400 }}>Handles inquiries 24/7/365</Typography>
                  </li>
                  <li style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
                    <span style={{ fontSize: 30, marginRight: 8 }}>📅</span>
                    <Typography variant="body2" sx={{ fontSize: 17, fontWeight: 400 }}>Scheduling & screening done for you</Typography>
                  </li>
                  <li style={{ display: 'flex', alignItems: 'center' }}>
                    <span style={{ fontSize: 30, marginRight: 8 }}>🏖️</span>
                    <Typography variant="body2" sx={{ fontSize: 17, fontWeight: 400 }}> Kick back and relax</Typography>
                  </li>
                </ul>
              </Box>
              <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', mt: 4, mb: 2, fontWeight: 350, justifyContent: 'center' }}>
                No listing created yet, no problem. We will create it for you.
              </Typography>
            </Box>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSuccess}
              sx={{
                mt: 2,
                backgroundColor: 'black',
                '&:hover': {
                  backgroundColor: '#333',
                },
              }}
            >
              Sounds Good!
            </Button>
          </>
        )}

      </Box>
    </ThemeProvider>
  );
};

export default ListingProcessingStep;